import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Download, Sunrise, UtensilsCrossed } from "lucide-react";
import { PageHero } from "@/components/Section";
import Reveal from "@/components/Reveal";
import MealCard from "@/components/MealCard";
import BreakfastCard from "@/components/BreakfastCard";
import NutritionMatrix from "@/components/NutritionMatrix";
import CTAButton from "@/components/CTAButton";
import { FormNotice } from "@/components/FormControls";
import { menuWeeks } from "@/data/menu";
import { breakfastIntro, breakfastGroups, breakfastItems } from "@/data/breakfast";
import { useBrand } from "@/context/BrandContext";

const tabs = [
  { id: "breakfast", label: "Breakfast", group: "Breakfast" },
  ...menuWeeks.map((w) => ({ id: `week-${w.number}`, label: `Week ${w.number}`, group: "Lunch" })),
  { id: "nutrition", label: "Nutrition", group: "Lunch" },
];

const lunchTabs = tabs.filter((t) => t.group === "Lunch");

const validTab = (id) => tabs.some((t) => t.id === id);

const BreakfastPanel = () => (
  <div data-testid="meals-breakfast-panel">
    <Reveal className="max-w-3xl">
      <p className="mbm-eyebrow">Breakfast</p>
      <h2 className="mt-3 font-heading text-2xl font-semibold tracking-tight sm:text-3xl lg:text-4xl">
        {breakfastIntro.title}
      </h2>
      <p className="mt-4 text-sm leading-relaxed text-cocoa/70 sm:text-base">
        {breakfastIntro.intro}
      </p>
      <div className="mt-6 flex flex-wrap items-center gap-3">
        <span
          data-testid="breakfast-status"
          className="inline-block rounded-full bg-amber px-4 py-1.5 text-xs font-extrabold uppercase tracking-[0.12em] text-cocoa"
        >
          {breakfastIntro.statusLabel}
        </span>
        <span className="mbm-chip">{breakfastItems.length} dishes in rotation</span>
      </div>
      <div className="mt-6 max-w-2xl">
        <FormNotice tone="warn" testId="breakfast-note">
          {breakfastIntro.note}
        </FormNotice>
      </div>
    </Reveal>

    {breakfastGroups.map((group) => (
      <div key={group.id} className="mt-14" data-testid={`breakfast-group-${group.id}`}>
        <Reveal className="max-w-3xl">
          <h3 className="font-heading text-xl font-semibold tracking-tight sm:text-2xl">
            {group.title}
          </h3>
          <p className="mt-3 text-sm leading-relaxed text-cocoa/70">{group.summary}</p>
        </Reveal>
        <div className="mt-8 grid gap-6 md:grid-cols-2 xl:grid-cols-3">
          {breakfastItems
            .filter((i) => i.group === group.id)
            .map((item, i) => (
              <Reveal key={item.id} delay={i * 60}>
                <BreakfastCard item={item} index={i} />
              </Reveal>
            ))}
        </div>
      </div>
    ))}

    <Reveal className="mt-14">
      <div className="mbm-card p-6 sm:p-8">
        <h3 className="font-heading text-lg font-semibold">
          Why there are no calorie figures on the breakfasts yet.
        </h3>
        <p className="mt-3 max-w-3xl text-sm leading-relaxed text-cocoa/70">
          Our standard is caloric and ingredient transparency for every meal. The breakfast recipes
          have only just been finalised, so the values are being worked out properly rather than
          estimated. They will be published here — and in the parent note that travels with every
          meal — before breakfast goes on sale.
        </p>
        <div className="mt-5">
          <CTAButton to="/food-standards" variant="outline" data-testid="breakfast-standards-cta">
            Read our food standards
          </CTAButton>
        </div>
      </div>
    </Reveal>
  </div>
);

const WeekPanel = ({ week }) => (
  <div data-testid={`meals-week-panel-${week.number}`}>
    <Reveal className="max-w-3xl">
      <p className="mbm-eyebrow">Lunch · Week {week.number}</p>
      <h2 className="mt-3 font-heading text-2xl font-semibold tracking-tight sm:text-3xl lg:text-4xl">
        {week.title}
      </h2>
      <p className="mt-4 text-sm leading-relaxed text-cocoa/70 sm:text-base">{week.summary}</p>
      <div className="mt-6 flex flex-wrap gap-2.5">
        {[
          ["Calories", week.dailyRange.calories],
          ["Protein", week.dailyRange.protein],
          ["Fibre", week.dailyRange.fibre],
        ].map(([label, value]) => (
          <span
            key={label}
            className="rounded-full bg-amber px-3.5 py-1.5 text-[12px] font-semibold text-cocoa"
          >
            {label} {value} per day
          </span>
        ))}
      </div>
    </Reveal>

    <div className="mt-12 grid gap-6 md:grid-cols-2 xl:grid-cols-3">
      {week.meals.map((meal, i) => (
        <Reveal key={meal.day} delay={i * 70}>
          <MealCard meal={meal} index={i} />
        </Reveal>
      ))}
    </div>
  </div>
);

const NutritionPanel = () => (
  <div data-testid="meals-nutrition-panel">
    <Reveal className="max-w-3xl">
      <p className="mbm-eyebrow">Lunch nutrition matrix</p>
      <h2 className="mt-3 font-heading text-2xl font-semibold tracking-tight sm:text-3xl lg:text-4xl">
        Every lunch, side by side.
      </h2>
      <p className="mt-4 text-sm leading-relaxed text-cocoa/70 sm:text-base">
        Compare approximate calories and protein for each day across the three lunch weeks. Breakfast
        values are published once they are confirmed.
      </p>
    </Reveal>
    <div className="mt-12">
      <NutritionMatrix />
    </div>
  </div>
);

export default function MealsPage() {
  const brand = useBrand();
  const [params, setParams] = useSearchParams();
  const [active, setActive] = useState(
    validTab(params.get("tab")) ? params.get("tab") : "breakfast"
  );

  const select = (id) => {
    setActive(id);
    setParams({ tab: id }, { replace: true });
  };

  const week = menuWeeks.find((w) => `week-${w.number}` === active);
  const meal = active === "breakfast" ? "breakfast" : "lunch";

  return (
    <>
      <PageHero
        eyebrow={`${brand.product.name} · breakfast & lunch, on a three-week rotation`}
        title="Every meal we make, in one place"
       
        intro={`${breakfastItems.length} breakfasts and fifteen lunches on a three-week rotation. Every dish lists its ingredients, common allergens, portion options, storage and reheating — and travels with a note for you and a note for your child.`}
      >
        <div className="flex flex-wrap items-center gap-3">
          <CTAButton to="/plans" data-testid="meals-plans-cta">
            See the meal plans
          </CTAButton>
          {brand.downloads.menuGuidePdf ? (
            <CTAButton
              href={brand.downloads.menuGuidePdf}
              download
              variant="secondary"
              data-testid="download-menu-guide"
            >
              <Download className="h-4 w-4" />
              Download Menu &amp; Nutrition Guide
            </CTAButton>
          ) : (
            <span
              data-testid="download-menu-guide-unavailable"
              className="inline-flex min-h-[48px] items-center gap-2 rounded-control border-2 border-dashed border-cocoa px-5 text-sm font-medium text-cocoa/65"
            >
              <Download className="h-4 w-4" />
              Menu &amp; Nutrition Guide — ask us for a copy
            </span>
          )}
        </div>
      </PageHero>

      <div className="sticky top-[68px] z-30 bg-cream/92 backdrop-blur-xl">
        <div className="mbm-container flex flex-col gap-3 py-3 sm:flex-row sm:items-center sm:gap-5">
          <div
            role="tablist"
            aria-label="Meal type"
            data-testid="meals-toggle"
            className="inline-flex w-full shrink-0 rounded-full bg-sageSoft p-1.5 sm:w-auto"
          >
            {[
              { id: "breakfast", label: "Breakfast", icon: Sunrise, count: breakfastItems.length },
              { id: "lunch", label: "Lunch", icon: UtensilsCrossed, count: 15 },
            ].map((m) => (
              <button
                key={m.id}
                role="tab"
                aria-selected={meal === m.id}
                data-testid={`meals-toggle-${m.id}`}
                onClick={() => select(m.id === "breakfast" ? "breakfast" : "week-1")}
                className={`inline-flex min-h-[48px] flex-1 items-center justify-center gap-2 whitespace-nowrap rounded-full px-3 text-sm font-extrabold transition-all duration-300 sm:flex-none sm:px-6 ${
                  meal === m.id
                    ? "bg-surface text-cocoa shadow-soft"
                    : "text-cocoa/75 hover:text-cocoa"
                }`}
              >
                <m.icon className="h-4 w-4" aria-hidden="true" />
                {m.label}
                <span className={meal === m.id ? "text-coralInk" : "text-cocoa/65"}>{m.count}</span>
              </button>
            ))}
          </div>

          {meal === "lunch" && (
            <div
              role="tablist"
              aria-label="Lunch weeks"
              className="-mx-1 flex items-center gap-2 overflow-x-auto px-1 pb-1 sm:pb-0"
            >
              {lunchTabs.map((t) => (
                <button
                  key={t.id}
                  role="tab"
                  aria-selected={active === t.id}
                  data-testid={`meals-tab-${t.id}`}
                  onClick={() => select(t.id)}
                  className={`inline-flex min-h-[48px] shrink-0 items-center rounded-full px-5 text-[13px] font-extrabold transition-colors ${
                    active === t.id
                      ? "bg-amber text-cocoa"
                      : "bg-surface text-cocoa/70 shadow-soft hover:text-cocoa"
                  }`}
                >
                  {t.label}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      <section className="py-14 sm:py-20">
        <div className="mbm-container">
          {active === "breakfast" && <BreakfastPanel />}
          {week && <WeekPanel week={week} />}
          {active === "nutrition" && <NutritionPanel />}
        </div>
      </section>
    </>
  );
}
